/**
 * Who has access.
 *
 * An owner adds a colleague and gets a link; the colleague sets their own
 * password. The owner never sees it — people reuse passwords, so an owner who set
 * one would probably have learned a credential that works elsewhere, and every
 * action on that account afterwards would be unattributable.
 *
 * Every judgement about what may be done to whom lives in userViewModel and is
 * tested there. The server enforces all of it regardless; this only avoids
 * offering a button that is going to be refused.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { EditAccess } from './EditAccess.tsx';
import {
  ApiError,
  fetchSites,
  fetchUsers,
  inviteUser,
  reinviteUser,
  removeUser,
  setUserRole,
  storedToken,
  type ApiUser,
  type IssuedInvite,
} from './api.ts';
import {
  ROLE_DESCRIPTION,
  ROLE_LABEL,
  userIdFromToken,
  usersViewModel,
  type UserRole,
  type UserRowModel,
} from './userViewModel.ts';

/*
 * Least to most capable. The order is the point: someone scanning this list
 * should read it as a ladder, and installer sits below operator because it
 * cannot destroy anything.
 */
const ROLES: UserRole[] = ['viewer', 'installer', 'operator', 'owner'];

/**
 * An invitation link, shown once.
 *
 * Held until dismissed, never auto-hidden. The code exists in exactly one
 * response and cannot be retrieved afterwards, so a toast that faded while
 * someone was copying it would lose it.
 */
function InviteLink({ invite, onDismiss }: { invite: IssuedInvite; onDismiss: () => void }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  // The fragment, deliberately: fragments are never sent to a server, so the code
  // stays out of access logs and out of Referer headers.
  const url = `${window.location.origin}/#/accept/${invite.inviteCode}`;

  return (
    <div className="card invite">
      <div className="card__body">
        <h3>Invitation for {invite.email}</h3>
        <p className="form__hint">
          Send this link to them. It is shown once and cannot be recovered — if it is lost, issue a
          new one. It expires {new Date(invite.inviteExpiresAt).toLocaleString()}.
        </p>
        <code className="invite__link">{url}</code>
        <div className="form__actions">
          <button
            type="button"
            className="btn btn--sm btn--primary"
            onClick={() => {
              void navigator.clipboard?.writeText(url).then(
                () => setCopied(true),
                // Clipboard access can be refused. The link is on screen either
                // way, so this must not look like a failure to create it.
                () => setCopied(false),
              );
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <button type="button" className="btn btn--sm" onClick={onDismiss}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function UserRow({
  row,
  record,
  onChanged,
  onInvited,
}: {
  row: UserRowModel;
  record: ApiUser;
  onChanged: () => void;
  onInvited: (invite: IssuedInvite) => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingAccess, setEditingAccess] = useState(false);

  async function act(work: () => Promise<void>, fallback: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await work();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="user-row">
      <div className="user-row__ident">
        <span className="user-row__email">
          {row.email}
          {row.isSelf ? <span className="pill pill--muted">You</span> : null}
        </span>
        <span className="user-row__activity">{row.activityLabel}</span>
        {/* Shown on every row, not hidden behind the editor: "who can see what"
            is the question this page exists to answer at a glance. */}
        <span className="user-row__access">{row.accessLabel}</span>
      </div>

      <div className="user-row__actions">
        <label className="user-row__role">
          <span className="visually-hidden">Role for {row.email}</span>
          <select
            value={row.role}
            disabled={busy || !row.canChangeRole}
            title={row.restriction ?? ROLE_DESCRIPTION[row.role]}
            onChange={(e) =>
              void act(
                () => setUserRole(row.id, e.target.value as UserRole),
                'Could not change the role.',
              )
            }
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABEL[role]}
              </option>
            ))}
          </select>
        </label>

        {row.canScope ? (
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy}
            onClick={() => setEditingAccess((open) => !open)}
          >
            {editingAccess ? 'Close' : 'Sites & cameras'}
          </button>
        ) : null}

        {row.canReinvite ? (
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy}
            onClick={() =>
              void act(async () => {
                onInvited(await reinviteUser(row.id));
              }, 'Could not issue a new invitation.')
            }
          >
            New invitation
          </button>
        ) : null}

        <button
          type="button"
          className="btn btn--sm btn--danger"
          disabled={busy || !row.canRemove}
          title={row.restriction ?? undefined}
          onClick={() => {
            if (!window.confirm(`Remove ${row.email}?\n\nThey lose access when their current session expires.`)) {
              return;
            }
            void act(() => removeUser(row.id), 'Could not remove the account.');
          }}
        >
          Remove
        </button>
      </div>

      {/* Shown rather than left to a disabled control with no explanation, which
          reads as a bug. */}
      {editingAccess ? (
        <EditAccess
          user={record}
          onSaved={() => {
            setEditingAccess(false);
            onChanged();
          }}
          onCancel={() => setEditingAccess(false)}
        />
      ) : null}

      {row.restriction ? <p className="user-row__note">{row.restriction}</p> : null}
      {error ? (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      ) : null}
    </li>
  );
}

export function Users({ now }: { now: number }): React.JSX.Element {
  const [users, setUsers] = useState<ApiUser[] | null>(null);
  const [siteCount, setSiteCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [invite, setInvite] = useState<IssuedInvite | null>(null);

  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('viewer');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers(await fetchUsers());
      setLoadError(null);
      // Only to say "every site (7)" rather than a bare "every site" — a count
      // makes the scale of unscoped access visible.
      void fetchSites().then((list) => setSiteCount(list.length), () => undefined);
    } catch (err) {
      /*
       * A viewer or operator reaching this page gets 403, which is a fact about
       * their role rather than a failure. Said plainly instead of as an error.
       */
      if (err instanceof ApiError && err.status === 403) {
        setLoadError('Only owners can manage people.');
        return;
      }
      setLoadError(err instanceof ApiError ? err.message : 'Could not load the people list.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);

    if (email.trim() === '') {
      setFormError('An email address is required.');
      return;
    }

    setBusy(true);
    try {
      setInvite(await inviteUser(email.trim(), role));
      setEmail('');
      setRole('viewer');
      setAdding(false);
      void load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not create the invitation.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError && users === null) {
    return (
      <section className="section">
        <div className="empty">
          <p className="empty__title">{loadError}</p>
        </div>
      </section>
    );
  }

  const model = usersViewModel(users ?? [], userIdFromToken(storedToken()), now, siteCount);

  return (
    <section className="section">
      {invite ? <InviteLink invite={invite} onDismiss={() => setInvite(null)} /> : null}

      <div className="section__head">
        <p className="section__meta">
          {model.ownerCount} {model.ownerCount === 1 ? 'owner' : 'owners'}
          {model.pendingCount > 0 ? ` · ${model.pendingCount} invitation pending` : ''}
        </p>
        {adding ? null : (
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setAdding(true)}>
            Invite someone
          </button>
        )}
      </div>

      {adding ? (
        <form className="card form" style={{ padding: 'var(--s4)' }} onSubmit={(e) => void submit(e)}>
          <div className="field">
            <label htmlFor="invite-email">Email address</label>
            <input
              id="invite-email"
              type="email"
              value={email}
              autoComplete="off"
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@example.com"
            />
          </div>

          <div className="field">
            <label htmlFor="invite-role">Role</label>
            <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
            {/* Beside the choice, because "operator" means nothing on its own. */}
            <span className="field__hint">{ROLE_DESCRIPTION[role]}</span>
          </div>

          <p className="form__hint">
            They choose their own password from the invitation link. You will not see it.
          </p>

          <div className="form__actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
              {busy ? 'Creating…' : 'Create invitation'}
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>

          {formError ? (
            <p className="notice notice--error" role="alert">
              {formError}
            </p>
          ) : null}
        </form>
      ) : null}

      {model.hasExpiredInvites ? (
        <p className="notice notice--warn" style={{ marginTop: 'var(--s4)' }}>
          An invitation has expired without being used. Issue a new one if that person still needs
          access.
        </p>
      ) : null}

      {users === null ? (
        <p className="empty">Loading…</p>
      ) : (
        <ul className="user-rows">
          {model.users.map((row) => (
            <UserRow
              key={row.id}
              row={row}
              record={(users ?? []).find((u) => u.id === row.id) as ApiUser}
              onChanged={() => void load()}
              onInvited={setInvite}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
